import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Button,
  Collapse,
  ListItem,
  useTheme,
  alpha
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

function NavItem({
  title,
  href,
  depth,
  children,
  icon: Icon,
  className,
  open: openProp,
  info: Info,
  role,
  activeRole,
  ...rest
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  let paddingLeft = 2;
  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;    
  }

  const hasAccess = !role || role.includes(activeRole);

  const baseButtonStyle = {
    color: theme.palette.text.secondary,    
    padding: '10px 8px',
    justifyContent: 'flex-start',
    alignItems: 'center', // Ensure vertical alignment
    textAlign: 'left',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: depth === 0 ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
    paddingLeft,
    '&.active': {
      color: theme.palette.secondary.main,
      fontWeight: theme.typography.fontWeightMedium,
      '& .NavItem-icon': {
        color: theme.palette.secondary.main,
      },
    },
  };

  const iconStyle = {
    display: 'flex',
    alignItems: 'center',
    mr: 1,
    minWidth: 20,    
  };

  const titleStyle = {
    marginRight: 'auto',
  };

  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{ display: 'block', flexDirection: 'column', py: 0 }}
        className={className}        
        {...rest}

      >
        <Button onClick={handleToggle} sx={baseButtonStyle}>
          {Icon && <Icon className="NavItem-icon" sx={iconStyle} size="20" />}
          <span style={titleStyle}>{title}</span>
          {open ? <ExpandLessIcon size="small" color="inherit" /> : <ExpandMoreIcon size="small" color="inherit" />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  if (!hasAccess) return null;

  return (
    <ListItem
      disableGutters
      sx={{ display: 'column', py: 0 }}
      className={className}
      {...rest}
    >
      <Button
        component={RouterLink}
        to={href}
        end
        sx={baseButtonStyle}
        className={clsx(`depth-${depth}`)}
      >
        {Icon && <Icon className="NavItem-icon" sx={iconStyle} size="20" />}
        <span style={titleStyle}>{title}</span>
        {Info && <Info />}
      </Button>
    </ListItem>
  );
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  info: PropTypes.any,
  open: PropTypes.bool,
  role: PropTypes.array,
  activeRole: PropTypes.string,
  title: PropTypes.string.isRequired
};

NavItem.defaultProps = {
  open: false,
  role: undefined
};

export default NavItem;
